import { z } from 'zod';
import { dateOnlySchema, idSchema } from './common';

export const MEMBER_IMPORT_MAX_ROWS = 5000;

export const MEMBER_IMPORT_FIELDS = ['firstName', 'lastName', 'phone', 'email', 'dateOfBirth', 'cardNumber', 'plan', 'endDate'] as const;
export type MemberImportField = (typeof MEMBER_IMPORT_FIELDS)[number];

const HEADER_NAMES: Record<MemberImportField, string[]> = {
  firstName: ['first name', 'firstname', 'first', 'given name', 'სახელი'],
  lastName: ['last name', 'lastname', 'last', 'surname', 'family name', 'გვარი'],
  phone: ['phone', 'phone number', 'mobile', 'mobile number', 'cell', 'tel', 'telephone', 'ტელეფონი', 'ტელეფონის ნომერი', 'ტელ', 'მობილური', 'მობილურის ნომერი', 'საკონტაქტო ნომერი'],
  email: ['email', 'e mail', 'email address', 'mail', 'ელფოსტა', 'ელ ფოსტა', 'ელექტრონული ფოსტა', 'იმეილი', 'მეილი'],
  dateOfBirth: ['date of birth', 'birth date', 'birthdate', 'birthday', 'dob', 'born', 'დაბადების თარიღი', 'დაბადების დღე', 'დაბ თარიღი', 'დაბადება'],
  cardNumber: ['card', 'card number', 'card no', 'card id', 'rfid', 'access card', 'ბარათი', 'ბარათის ნომერი', 'ბარათის'],
  plan: ['plan', 'membership', 'membership type', 'membership plan', 'package', 'subscription', 'tariff', 'გეგმა', 'აბონემენტი', 'აბონემენტის ტიპი', 'აბონემენტის სახეობა', 'პაკეტი', 'ტარიფი'],
  endDate: ['end date', 'end', 'ends', 'expiry', 'expiry date', 'expires', 'expiration date', 'valid until', 'valid to', 'membership end', 'ვადა', 'მოქმედების ვადა', 'დასრულების თარიღი', 'ვადის გასვლა', 'ვადის გასვლის თარიღი', 'აბონემენტის ვადა'],
};

const WHOLE_HEADER_ONLY = new Set(['first', 'last', 'end', 'ends', 'born', 'mail', 'cell', 'tel', 'ტელ']);

const normalizeHeader =(header: string) => header.toLowerCase().replace(/[._\-:/()#№*]+/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * Which column of the file holds each field, read from the headers in English or Georgian: an exact name first, then a
 * header that contains a name ("Mobile phone number"), the longest name winning, so "Membership end date" is the end date.
 */
export function suggestMemberImportColumns(headers: readonly string[]): Partial<Record<MemberImportField, number>> {
  const names = headers.map(normalizeHeader);
  const chosen: Partial<Record<MemberImportField, number>> = {};
  const taken = new Set<number>();
  const choose = (field: MemberImportField, column: number) => {
    if (chosen[field] !== undefined || taken.has(column)) return;
    chosen[field] = column;
    taken.add(column);
  };
  for (const field of MEMBER_IMPORT_FIELDS) {
    const column = names.findIndex((name, i) => !taken.has(i) && HEADER_NAMES[field].includes(name));
    if (column >= 0) choose(field, column);
  }
  const partial = MEMBER_IMPORT_FIELDS.flatMap((field) =>
    names.flatMap((name, column) => {
      const length = Math.max(0, ...HEADER_NAMES[field].filter((known) => !WHOLE_HEADER_ONLY.has(known) && ` ${name} `.includes(` ${known} `)).map((known) => known.length));
      return length > 0 ? [{ field, column, length }] : [];
    }),
  );
  for (const { field, column } of partial.sort((a, b) => b.length - a.length)) choose(field, column);
  return chosen;
}

export const MEMBER_IMPORT_OUTCOMES = ['NEW', 'CREATED', 'MATCHED', 'CARD_HELD', 'INVALID'] as const;
export type MemberImportOutcome = (typeof MEMBER_IMPORT_OUTCOMES)[number];

export const MEMBER_IMPORT_PROBLEMS = [
  'FIRST_NAME_MISSING',
  'LAST_NAME_MISSING',
  'NAME_TOO_LONG',
  'EMAIL_INVALID',
  'PHONE_INVALID',
  'BIRTH_DATE_INVALID',
  'CARD_INVALID',
  'NOTHING_TO_MATCH',
  'END_DATE_INVALID',
  'END_DATE_MISSING',
  'PLAN_MISSING',
] as const;
export type MemberImportProblem = (typeof MEMBER_IMPORT_PROBLEMS)[number];

const cell = z.string().max(500).optional();

export const memberImportRowSchema = z.object({
  line: z.number().int().min(1).describe('The line of the file the row came from, so the report can point at it'),
  firstName: cell,
  lastName: cell,
  phone: cell,
  email: cell,
  dateOfBirth: cell.describe('ISO `YYYY-MM-DD` or `DD.MM.YYYY` / `DD/MM/YYYY`; a date that cannot be read is a problem, never a guess'),
  cardNumber: cell.describe('Read in the canonical card-number form, so a zero-padded number matches the card it is'),
  plan: cell.describe("A plan's name in any of its languages; an unknown name becomes a hidden plan of its own, as promotion from the old system does"),
  endDate: cell.describe('The last day of the membership, in the same forms as the date of birth'),
});
export type MemberImportRow = z.infer<typeof memberImportRowSchema>;

export const memberImportSchema = z.object({
  rows: z.array(memberImportRowSchema).min(1).max(MEMBER_IMPORT_MAX_ROWS),
});
export type MemberImportInput = z.infer<typeof memberImportSchema>;

export const memberImportMatchSchema = z.object({
  by: z.enum(['PHONE', 'EMAIL', 'CARD']),
  memberId: idSchema.nullable(),
  memberNumber: z.string().nullable(),
  name: z.string().nullable(),
  line: z.number().int().nullable().describe('An earlier row of the same file, when that is what this row ran into'),
});
export type MemberImportMatchDto = z.infer<typeof memberImportMatchSchema>;

export const memberImportMembershipSchema = z.object({
  planId: idSchema.nullable().describe('Null while the plan is one this import would make'),
  planName: z.string(),
  newPlan: z.boolean().describe('No plan of the gym carries this name: a hidden, unpurchasable plan is made for it'),
  endsOn: dateOnlySchema,
  status: z.enum(['ACTIVE', 'EXPIRED']),
  given: z.boolean().describe('False when the importer cannot give memberships without a payment (memberships.comp): the member comes in without it'),
});
export type MemberImportMembershipDto = z.infer<typeof memberImportMembershipSchema>;

export const memberImportRowResultSchema = z.object({
  line: z.number().int(),
  outcome: z.enum(MEMBER_IMPORT_OUTCOMES),
  problems: z.array(z.enum(MEMBER_IMPORT_PROBLEMS)),
  match: memberImportMatchSchema.nullable().describe('The member, or earlier row, a MATCHED row is; the holder of the card on a CARD_HELD row'),
  membership: memberImportMembershipSchema.nullable(),
  memberId: idSchema.nullable().describe('The member a CREATED row became'),
});
export type MemberImportRowResultDto = z.infer<typeof memberImportRowResultSchema>;

export const memberImportReportSchema = z.object({
  dryRun: z.boolean(),
  counts: z.object({
    rows: z.number().int(),
    new: z.number().int(),
    created: z.number().int(),
    matched: z.number().int(),
    cardHeld: z.number().int(),
    invalid: z.number().int(),
  }),
  memberships: z.object({ given: z.number().int(), leftOut: z.number().int() }),
  canGiveMemberships: z.boolean().describe('Whether the importer holds memberships.comp: an imported membership is not a sale, so nobody without it brings one in'),
  newPlans: z.array(z.string()).describe('Hidden plans the import makes (dry run) or made'),
  rows: z.array(memberImportRowResultSchema),
});
export type MemberImportReportDto = z.infer<typeof memberImportReportSchema>;
